import { create } from 'zustand';
import { client, MailClient, onSessionEnded } from '../api/client';
import { useToasts } from '../ui/toasts';

/*
    Who is signed in.

    Several mailboxes at once, each with a session of its own. The server gives every mailbox its
    own cookie (named after the address), so signing in to a second one no longer replaces the
    first; each request says which mailbox it is for, and the server picks that cookie. The mail
    server itself still has no notion of delegated access: signing in to a shared info@ means
    signing in as info@, with info@'s password — this only saves signing out and back in.

    There is still no token here, nothing kept in localStorage, and nothing to refresh. The cookies
    are the state, and script cannot read them; so on a reload the list is asked for, not
    remembered (GET /api/sessions). A 401 on any request means THAT mailbox's session is over.
*/

export interface Account {
    /** The address, which doubles as the id: a session belongs to one mailbox. */
    id: string;
    email: string;
    /**
     * The same address again.
     *
     * JMAP had a separate account id inside a session, and the screens are written to pass one
     * around. Here there is only the mailbox, so this is the address — kept rather than removed
     * so that every call site does not have to change to say the same thing a different way.
     */
    accountId: string;
    /** Index into the avatar palette. */
    color: number;
    /**
     * Whether THIS mailbox administers a domain on the management service.
     *
     * A property of the mailbox rather than of the installation: the same deployment answers
     * differently for different people, and with two mailboxes open in one browser it answers
     * differently for each tab of the switcher. The server settles it once, at sign-in.
     */
    hasAdmin: boolean;
}

type Status = 'idle' | 'connecting' | 'ready' | 'error';

/**
 * What this deployment can do, as the server answers it.
 *
 * Carried together rather than as a widening list of booleans on every call: two of them is
 * already one pair too many to pass positionally, and the third would be passed in the wrong
 * order by somebody one day.
 */
export interface Capabilities {
    canPreviewDocuments?: boolean;
    hasDAV?: boolean;
    hasForwarding?: boolean;
    hasAdmin?: boolean;
}

interface AccountsState {
    accounts: Account[];
    activeId: string | null;
    status: Record<string, Status>;
    errors: Record<string, string>;
    /** True until the first check with the server has finished, so the login form does not flash. */
    checking: boolean;
    /**
     * Whether this deployment can show office documents rather than only offer them for saving.
     *
     * A property of the server, not of the mailbox: it depends on whether a converter is
     * configured beside it. Kept here because the reader has to know before it draws an
     * attachment, and a control that cannot work is left out rather than shown.
     */
    canPreviewDocuments: boolean;
    /**
     * Whether this deployment has an address book behind it.
     *
     * Also a property of the server rather than of the mailbox — it depends on whether a DAV
     * server is configured beside it. Without one there is no address book screen at all: the
     * navigation does not offer it, rather than offering a tab that opens onto an apology.
     */
    hasDAV: boolean;
    /**
     * Whether this deployment has a ManageSieve server behind it, which is what makes forwarding
     * and the automatic reply possible.
     *
     * A flag of its own rather than a second meaning on hasDAV: they are different servers
     * and either can be absent without the other. Without one the settings screen does not offer
     * the tab at all.
     */
    hasForwarding: boolean;
    /**
     * Mailboxes whose session the server ended under this tab — a deploy, an expiry, a password
     * reset. Kept so the sign-in form can say why it is there and fill the address in.
     */
    ended: string[];
    /** Whether a composer is open. While it is, an ended session is not acted on — see sessionEnded. */
    composing: boolean;

    bootstrap: () => Promise<void>;
    /** Adds a mailbox that has just signed in, or refreshes it if it was already in the list. */
    adopt: (address: string, caps?: Capabilities) => Account;
    /** Signs that one mailbox out; the others stay signed in. */
    remove: (id: string) => void;
    setActive: (id: string) => void;
    /** A request for this mailbox came back 401. */
    sessionEnded: (address: string) => void;
    setComposing: (on: boolean) => void;
}

/*
    One client per mailbox, because the client is what says which mailbox a request is for (and
    it keeps a per-mailbox cache of what it has already fetched). Kept outside the store: a client
    is not state a screen redraws on, and a new one per render would throw that cache away.

    Without an id — only the sign-in form asks for that — the client names no mailbox, which the
    server accepts for signing in and nothing else once several are open.
*/
const clients = new Map<string, MailClient>();
export const clientFor = (id?: string): MailClient => {
    if (!id) return client;
    const key = id.toLowerCase();
    let c = clients.get(key);
    if (!c) {
        c = new MailClient('/api', id);
        clients.set(key, c);
    }
    return c;
};

/** Whether the mailbox on screen administers a domain. */
export const activeHasAdmin = (s: AccountsState): boolean =>
    Boolean(s.accounts.find((a) => a.id === s.activeId)?.hasAdmin);

const accountOf = (address: string, hasAdmin: boolean): Account => ({
    id: address,
    email: address,
    accountId: address,
    color: 0,
    hasAdmin,
});

export const useAccounts = create<AccountsState>((set, get) => ({
    accounts: [],
    activeId: null,
    status: {},
    errors: {},
    checking: true,
    canPreviewDocuments: false,
    hasDAV: false,
    hasForwarding: false,
    ended: [],
    composing: false,

    /**
     * Ask the server who is signed in.
     *
     * The cookies outlive the page, so a reload has to find out which sessions are still good.
     * There is nothing stored on this side to go by — which is the point, since anything stored
     * here could be stale or wrong, and the server is the only thing that actually knows.
     */
    bootstrap: async () => {
        try {
            const open = await client.sessions();
            const accounts = open.sessions.map((s) => accountOf(s.address, s.hasAdmin));
            const previous = get().activeId;
            // Mailboxes this tab had and the server no longer does. Worked out in the same update
            // that drops them: set a moment later, the sign-in form would already be on screen
            // with nothing to say and no address to fill in.
            const gone = get().accounts.filter((a) => !accounts.some((b) => b.id === a.id)).map((a) => a.id);
            set({
                accounts,
                ended: gone.length ? gone : get().ended,
                activeId: accounts.find((a) => a.id === previous)?.id ?? accounts[0]?.id ?? null,
                status: Object.fromEntries(accounts.map((a) => [a.id, 'ready' as Status])),
                checking: false,
                canPreviewDocuments: Boolean(open.canPreviewDocuments),
                hasDAV: Boolean(open.hasDAV),
                hasForwarding: Boolean(open.hasForwarding),
            });
        } catch (e) {
            set({
                checking: false,
                errors: { session: e instanceof Error ? e.message : String(e) },
            });
        }
    },

    adopt: (address, caps = {}) => {
        const { accounts, status, errors } = get();
        const same = (a: Account) => a.id.toLowerCase() === address.toLowerCase();
        const old = accounts.find(same);
        // Signing in again to a mailbox already in the list replaces its session on the server;
        // here it keeps its place rather than moving to the end.
        const account = accountOf(address, Boolean(caps.hasAdmin));
        const next = old ? accounts.map((a) => (same(a) ? account : a)) : [...accounts, account];
        const restErrors = { ...errors };
        delete restErrors[account.id];
        delete restErrors.session;
        set({
            accounts: next,
            activeId: account.id,
            status: { ...status, [account.id]: 'ready' },
            errors: restErrors,
            ended: get().ended.filter((a) => a.toLowerCase() !== account.id.toLowerCase()),
            checking: false,
            canPreviewDocuments: Boolean(caps.canPreviewDocuments),
            hasDAV: Boolean(caps.hasDAV),
            hasForwarding: Boolean(caps.hasForwarding),
        });
        return account;
    },

    remove: (id) => {
        void clientFor(id)
            .signOut()
            .catch(() => {
                /* the session is being abandoned either way */
            });
        clients.delete(id.toLowerCase());
        const { accounts, activeId, status, errors } = get();
        const rest = accounts.filter((a) => a.id !== id);
        const restStatus = { ...status };
        delete restStatus[id];
        const restErrors = { ...errors };
        delete restErrors[id];
        set({
            accounts: rest,
            activeId: activeId === id ? (rest[0]?.id ?? null) : activeId,
            status: restStatus,
            errors: restErrors,
        });
    },

    setActive: (id) => set({ activeId: id }),

    /*
        Not "drop the mailbox": ask the server which sessions are still open, and let that answer
        decide. A 401 says this request failed; the session list says what is true now — and it
        is the same question a reload asks, so the screen ends up where a reload would put it.

        Not while a composer is open. Leaving the mail screen would take what is being written
        with it, and the composer already says the session is gone and to sign in again in
        another tab — which revives the session, since the cookie is per mailbox. So the check
        waits for the composer to close, and by then the answer may well be "still open".
    */
    sessionEnded: (address) => {
        const { accounts, composing } = get();
        if (!accounts.some((a) => a.id.toLowerCase() === address.toLowerCase())) return;
        if (composing) {
            deferred = true;
            return;
        }
        void recheck();
    },

    setComposing: (on) => {
        if (get().composing === on) return;
        set({ composing: on });
        if (!on && deferred) {
            deferred = false;
            void recheck();
        }
    },
}));

/*
    One question at a time. A deploy ends every session at once, and a screen that was showing a
    folder list, a message list and a message fires three requests that all come back 401; they
    share one answer rather than asking three times.
*/
let rechecking: Promise<void> | null = null;
let deferred = false;

function recheck(): Promise<void> {
    rechecking ??= (async () => {
        const before = useAccounts.getState().accounts.map((a) => a.id);
        await useAccounts.getState().bootstrap();
        const { accounts } = useAccounts.getState();
        const gone = before.filter((id) => !accounts.some((a) => a.id === id));
        if (!gone.length) return;
        for (const id of gone) clients.delete(id.toLowerCase());
        // With another mailbox still open the screen moves to it, and only this line says why;
        // with none left the sign-in form says it instead.
        if (accounts.length) useToasts.getState().show({ message: 'session.ended', values: { address: gone.join(', ') } });
    })().finally(() => {
        rechecking = null;
    });
    return rechecking;
}

onSessionEnded((mailbox) => useAccounts.getState().sessionEnded(mailbox));
